/**
 * One field-report card: operator facts, system enrichment, capture geometry.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { FieldReportCardModel } from './fieldReportView';
import FieldPhotoReview from './FieldPhotoReview';
import { useFieldReportPhoto } from './useFieldReportPhoto';
import { metalSx } from '../../theme/metalButtons';

const GOLD = '#f5b942';
const MUTED = '#8a939f';
const EDGE = '#2a3038';
const PAPER = 'rgba(255,255,255,0.03)';

export interface FieldReportCardProps {
  model: FieldReportCardModel;
  hasPhoto: boolean;
  hasAnnotated?: boolean;
  reportId: string;
  onFocus?: () => void;
  onPromote?: () => void;
  onLinkRelated?: () => void;
  onRelook?: () => void;
  promoting?: boolean;
  relooking?: boolean;
}

const Row: React.FC<{ label: string; children: React.ReactNode; tone?: string }> = ({
  label, children, tone,
}) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', py: 0.18 }}>
    <Typography sx={{ width: 108, flexShrink: 0, fontSize: '0.58rem', color: MUTED }}>
      {label}
    </Typography>
    <Typography sx={{ flex: 1, fontSize: '0.66rem', color: tone || '#e8ebee', fontWeight: tone ? 700 : 500 }}>
      {children}
    </Typography>
  </Box>
);

const Head: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{
    mt: 1.15,
    mb: 0.4,
    fontSize: '0.58rem',
    fontWeight: 800,
    letterSpacing: 0.45,
    textTransform: 'uppercase',
    color: GOLD,
  }}
  >
    {children}
  </Typography>
);

const Btn: React.FC<{
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, children }) => (
  <Box
    component="button"
    type="button"
    disabled={disabled || !onClick}
    onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    sx={{
      ...metalSx('gun'),
      height: 26,
      px: 1.05,
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: '0.58rem',
      fontWeight: 600,
      fontFamily: 'inherit',
      cursor: disabled || !onClick ? 'default' : 'pointer',
      opacity: disabled || !onClick ? 0.45 : 1,
    }}
  >
    {children}
  </Box>
);

const Pane: React.FC<{
  label: string;
  src: string | null;
  waiting?: string;
  meta?: string;
  onOpen?: () => void;
  onPixels?: (size: string) => void;
  onBroken?: () => void;
}> = ({ label, src, waiting, meta, onOpen, onPixels, onBroken }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  const live = !!src && !broken;
  return (
  <Box
    role={live || onOpen || onBroken ? 'button' : undefined}
    tabIndex={live || onOpen || onBroken ? 0 : undefined}
    onClick={() => {
      if (live) onOpen?.();
      else onBroken?.();
    }}
    onKeyDown={(e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      if (live) onOpen?.();
      else onBroken?.();
    }}
    sx={{
      position: 'relative',
      flex: 1,
      minWidth: 0,
      height: 220,
      bgcolor: '#0b0d10',
      cursor: live || onOpen || onBroken ? 'pointer' : 'default',
    }}
  >
    {live ? (
      <Box
        component="img"
        src={src}
        alt={label}
        onLoad={(e) => {
          const img = e.currentTarget;
          if (img.naturalWidth) onPixels?.(`${img.naturalWidth}×${img.naturalHeight}`);
        }}
        onError={() => setBroken(true)}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          maxWidth: 'none',
          maxHeight: 'none',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    ) : (
      <Typography sx={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
        justifyContent: 'center', color: MUTED, fontSize: '0.58rem', px: 1, textAlign: 'center',
      }}
      >
        {broken ? 'Could not display photo — tap to retry' : (waiting || 'Loading photo…')}
      </Typography>
    )}
    <Typography sx={{
      position: 'absolute',
      left: 8,
      bottom: 6,
      right: 8,
      zIndex: 1,
      fontSize: '0.52rem',
      fontWeight: 700,
      letterSpacing: 0.3,
      color: 'rgba(232,235,238,0.88)',
      textShadow: '0 1px 2px #000',
    }}
    >
      {label}{meta ? ` · ${meta}` : ''}
    </Typography>
  </Box>
  );
};

const FieldReportCard: React.FC<FieldReportCardProps> = ({
  model,
  hasPhoto,
  hasAnnotated,
  reportId,
  onFocus,
  onPromote,
  onLinkRelated,
  onRelook,
  promoting,
  relooking,
}) => {
  const [preview, setPreview] = useState<'original' | 'annotated' | null>(null);
  const [pixels, setPixels] = useState<string | null>(null);
  const originalPhoto = useFieldReportPhoto(reportId, 'original', hasPhoto);
  const annotatedPhoto = useFieldReportPhoto(
    reportId,
    'annotated',
    hasPhoto && hasAnnotated !== false,
  );

  return (
    <Box
      sx={{
        mb: 1.25,
        border: `1px solid ${EDGE}`,
        borderRadius: 1.25,
        bgcolor: PAPER,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.1,
          py: 0.65,
          borderBottom: `1px solid ${EDGE}`,
        }}
      >
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, flex: 1, minWidth: 0 }} noWrap>
          {model.reportId}
        </Typography>
        <Box sx={{
          px: 0.7,
          height: 16,
          borderRadius: 4,
          border: `1px solid ${model.priority === 'PRIORITY' ? 'rgba(245,185,66,0.45)' : EDGE}`,
          color: model.priority === 'PRIORITY' ? GOLD : MUTED,
          fontSize: '0.52rem',
          fontWeight: 800,
          letterSpacing: 0.5,
          display: 'inline-flex',
          alignItems: 'center',
        }}
        >
          {model.priority}
        </Box>
        <Typography sx={{ fontSize: '0.58rem', color: MUTED, fontFamily: 'ui-monospace, monospace' }}>
          {model.dtg}
        </Typography>
      </Box>

      {hasPhoto && (
        <Box sx={{ display: 'flex', borderBottom: `1px solid ${EDGE}` }}>
          <Pane
            label="Original"
            src={originalPhoto.src}
            waiting={originalPhoto.loading ? 'Loading photo…' : (originalPhoto.error || 'No photo')}
            meta={[pixels, `${model.photoIndex} of ${model.photoTotal}`].filter(Boolean).join(' · ')}
            onOpen={originalPhoto.src
              ? () => setPreview('original')
              : originalPhoto.error ? originalPhoto.retry : undefined}
            onPixels={setPixels}
            onBroken={originalPhoto.retry}
          />
          <Box sx={{ width: 1, bgcolor: EDGE, flexShrink: 0 }} />
          <Pane
            label="Identified"
            src={annotatedPhoto.src}
            waiting={annotatedPhoto.loading
              ? 'Loading identified frame…'
              : (annotatedPhoto.error || 'Identification pending')}
            onOpen={annotatedPhoto.src ? () => setPreview('annotated') : undefined}
            onBroken={annotatedPhoto.retry}
          />
        </Box>
      )}
      {model.filing && (
        <Typography sx={{ px: 1.1, pt: 0.45, fontSize: '0.55rem', color: MUTED }}>
          Captured {model.dtg} — {model.filing}
        </Typography>
      )}

      <Box sx={{ px: 1.1, pb: 1.1 }} onClick={onFocus}>
        <Head>Observed — operator reported</Head>
        <Row label="Category">{model.observed.category}</Row>
        {model.observed.identifier && <Row label="Identifier">{model.observed.identifier}</Row>}
        <Row label="Duration on site">{model.observed.duration}</Row>
        {model.observed.note && (
          <Typography sx={{
            mt: 0.45,
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: '0.72rem',
            fontStyle: 'italic',
            fontWeight: 600,
            lineHeight: 1.45,
            color: '#f3f6f8',
          }}
          >
            “{model.observed.note}”
          </Typography>
        )}
        {model.observed.attribution && (
          <Typography sx={{ mt: 0.25, fontSize: '0.55rem', color: MUTED }}>
            {model.observed.attribution}
          </Typography>
        )}

        <Head>Assessed — system enrichment, not operator-reported</Head>
        {model.assessed.alias && <Row label="Alias">{model.assessed.alias}</Row>}
        {model.assessed.aka && <Row label="AKA">{model.assessed.aka}</Row>}
        {model.assessed.scene && <Row label="Scene">{model.assessed.scene}</Row>}
        {model.assessed.plate && (
          <Row label="Plate" tone={model.assessed.plateNote ? GOLD : undefined}>
            {model.assessed.plate}
            {model.assessed.plateNote ? ` · ${model.assessed.plateNote}` : ''}
          </Row>
        )}
        {model.assessed.identified && model.assessed.identified.length > 0 && (
          <Row label="Identified" tone={model.assessed.watchlisted ? GOLD : undefined}>
            {model.assessed.identified.join(', ')}
            {model.assessed.watchlisted ? ' · watchlisted' : ''}
          </Row>
        )}
        {model.assessed.prior && <Row label="Prior sightings">{model.assessed.prior}</Row>}
        {model.assessed.matchBasis && <Row label="Match basis">{model.assessed.matchBasis}</Row>}
        {model.assessed.warning && (
          <Box sx={{
            mt: 0.55,
            px: 0.8,
            py: 0.5,
            borderRadius: 0.75,
            bgcolor: 'rgba(245,185,66,0.08)',
            border: '1px solid rgba(245,185,66,0.28)',
          }}
          >
            <Typography sx={{ fontSize: '0.6rem', color: GOLD, fontWeight: 700 }}>
              {model.assessed.warning}
            </Typography>
          </Box>
        )}

        <Head>Capture geometry</Head>
        {model.geometry.observerMgrs && <Row label="Observer">{model.geometry.observerMgrs}</Row>}
        <Row label="Decimal">{model.geometry.decimal}</Row>
        {model.geometry.accuracy && <Row label="GPS accuracy">{model.geometry.accuracy}</Row>}
        {model.geometry.route && <Row label="Route context">{model.geometry.route}</Row>}

        <Box sx={{ display: 'flex', gap: 2, mt: 1.1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.55rem', color: MUTED }}>Source grading</Typography>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800 }}>{model.source.grade}</Typography>
            <Typography sx={{ fontSize: '0.55rem', color: MUTED }}>{model.source.label}</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.55rem', color: MUTED }}>Assessment confidence</Typography>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800 }}>{model.confidence.label}</Typography>
            <Typography sx={{ fontSize: '0.55rem', color: MUTED }}>{model.confidence.detail}</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.55, mt: 1, flexWrap: 'wrap' }}>
          <Btn onClick={onPromote} disabled={promoting}>
            {promoting ? 'Promoting…' : 'Promote to intel packet'}
          </Btn>
          <Btn onClick={onLinkRelated} disabled={!model.relatedId}>
            {model.relatedReportId ? `Link to ${model.relatedReportId}` : 'No linked report'}
          </Btn>
          <Btn onClick={onRelook} disabled={relooking}>
            {relooking ? 'Asking…' : 'Request re-look'}
          </Btn>
        </Box>
      </Box>

      <FieldPhotoReview
        open={!!preview}
        src={preview === 'annotated' ? annotatedPhoto.src : originalPhoto.src}
        title={`${model.reportId} · ${preview === 'annotated' ? 'identified' : 'original'}`}
        alt={model.observed.note || model.title}
        swapLabel={preview === 'annotated' ? 'Show original' : 'Show identified'}
        onSwap={originalPhoto.src && annotatedPhoto.src
          ? () => setPreview((which) => (which === 'annotated' ? 'original' : 'annotated'))
          : undefined}
        onClose={() => setPreview(null)}
      />
    </Box>
  );
};

export default FieldReportCard;
